import React, { useEffect, useMemo, useState } from 'react';
import { envSciQuestions } from '../../data/questions';
import { ENV_SCI_CONTENT_GROUPS, envSciTopicToGroup } from '../../data/envSciTopics';

const API_BASE = process.env.REACT_APP_API_URL;

const GEOGRAPHY_OPTIONS = [
  {
    value: 'physical',
    label: 'Physical Geography',
    topicsFour: 'Water & Carbon · Hot Deserts · Hazards',
    topicsFull: 'Paper 1 — Water & Carbon · Hot Deserts · Hazards',
  },
  {
    value: 'human',
    label: 'Human Geography',
    topicsFour: 'Global Governance & Systems · Resource Security · Changing Places',
    topicsFull: 'Paper 2 — Global Systems · Changing Places · Resource Security',
  },
];

const GEOGRAPHY_MOCK_TYPES = [
  { value: 'four_marker', label: '4-Marker Sprint', description: '6 questions · 25 min' },
  { value: 'full_mock',   label: 'Full Paper Mock', description: '~120 marks · 2 hr 30 min' },
];

const ENV_MOCK_TYPES = [
  { value: 'short_sprint', label: 'Short Sprint', description: '6 short questions · 25 min' },
  { value: 'mixed_mock',   label: 'Mixed Mock',   description: '5 questions · 3 short + 1×9m + 1×25m · 90 min' },
  { value: 'topic_mock',   label: 'Topic Mock',   description: '30 marks · 45 min · pick a content group · 2×9m + ~12m of shorts' },
];

const GCSE_SCIENCE_MOCK_TYPES = [
  { value: 'short_sprint', label: 'Short Sprint', description: '6 questions · mixed marks · 25 min' },
  { value: 'mixed_mock',   label: 'Mixed Mock',   description: '5 short + 1×6-mark extended · 35 min' },
];

const GCSE_SCIENCE_PAPER_OPTIONS = [
  { value: null, label: 'Both Papers',  description: 'Draw from the full question bank' },
  { value: 1,    label: 'Paper 1 only', description: 'Topics from the first exam paper' },
  { value: 2,    label: 'Paper 2 only', description: 'Topics from the second exam paper' },
];

const FOUR_MARKER_RULES = [
  { icon: '⏱', text: '25 minutes — the clock starts when you click Start' },
  { icon: '6',  text: '6 four-mark questions, randomly selected from all years' },
  { icon: '⚡', text: 'Auto-submits when time runs out — no exceptions' },
  { icon: '🚫', text: 'No hints, no model answers, no second chances' },
  { icon: '🤖', text: 'AI marks all 6 answers at once after submission' },
];

const FULL_MOCK_RULES = [
  { icon: '⏱', text: '2 hours 30 minutes — the clock starts when you click Start' },
  { icon: '📋', text: 'Representative question set across all mark weights (4, 6, 9 and 20 marks)' },
  { icon: '⚡', text: 'Auto-submits when time runs out — no exceptions' },
  { icon: '🚫', text: 'No hints, no model answers, no second chances' },
  { icon: '🤖', text: 'AI marks all answers at once after submission' },
];

const ENV_SHORT_RULES = [
  { icon: '⏱', text: '25 minutes — the clock starts when you click Start' },
  { icon: '6',  text: '6 short-answer questions (1–5 marks), randomly selected' },
  { icon: '⚡', text: 'Auto-submits when time runs out — no exceptions' },
  { icon: '🚫', text: 'No hints, no model answers, no second chances' },
  { icon: '🤖', text: 'AI marks all 6 answers at once after submission' },
];

const ENV_MIXED_RULES = [
  { icon: '⏱', text: '90 minutes — the clock starts when you click Start' },
  { icon: '📋', text: '5 questions: 3 short-answer + 1×9-mark + 1×25-mark' },
  { icon: '⚡', text: 'Auto-submits when time runs out — no exceptions' },
  { icon: '🚫', text: 'No hints, no model answers, no second chances' },
  { icon: '🤖', text: 'AI marks all answers at once after submission' },
];

const ENV_TOPIC_RULES = [
  { icon: '⏱', text: '45 minutes — clock starts on Start; 5-minute warning shows in the wizard' },
  { icon: '📋', text: '30 marks: 2×9-mark + ~12 marks of shorts, randomised across all topics in your chosen group' },
  { icon: '🆕', text: 'Prioritises questions you haven\'t answered; least-recent ones as tiebreaker' },
  { icon: '🔄', text: '20/25-mark essays may appear, rescaled and marked out of 9' },
  { icon: '⚡', text: 'Auto-submits when time runs out — no exceptions' },
  { icon: '🤖', text: 'AI marks all answers at once after submission' },
];

const GCSE_SCIENCE_SHORT_RULES = [
  { icon: '⏱', text: '25 minutes — the clock starts when you click Start' },
  { icon: '6',  text: '6 questions (structured, calculation, and short-answer), randomly selected from past papers' },
  { icon: '⚡', text: 'Auto-submits when time runs out — no exceptions' },
  { icon: '🚫', text: 'No hints, no model answers, no second chances' },
  { icon: '🤖', text: 'AI marks all 6 answers at once after submission' },
];

const GCSE_SCIENCE_MIXED_RULES = [
  { icon: '⏱', text: '35 minutes — the clock starts when you click Start' },
  { icon: '📋', text: '6 questions: 5 short/structured/calculation + 1 extended 6-marker' },
  { icon: '⚡', text: 'Auto-submits when time runs out — no exceptions' },
  { icon: '🚫', text: 'No hints, no model answers, no second chances' },
  { icon: '🤖', text: 'AI marks all answers at once after submission' },
];

const GCSE_SCIENCE_SUBJECTS = new Set(['biology', 'chemistry', 'physics']);

function getRules(subject, mockType) {
  if (GCSE_SCIENCE_SUBJECTS.has(subject)) {
    return mockType === 'mixed_mock' ? GCSE_SCIENCE_MIXED_RULES : GCSE_SCIENCE_SHORT_RULES;
  }
  if (subject === 'env-science') {
    if (mockType === 'mixed_mock') return ENV_MIXED_RULES;
    if (mockType === 'topic_mock') return ENV_TOPIC_RULES;
    return ENV_SHORT_RULES;
  }
  return mockType === 'full_mock' ? FULL_MOCK_RULES : FOUR_MARKER_RULES;
}

// Content groups that actually appear in the env-sci question bank. Disable
// groups whose true-9 + recast-25 pool is below 2 questions across all topics
// in the group (see useMockExam.selectEnvSciTopicMock).
function buildEnvSciContentGroupOptions() {
  const stats = new Map();
  for (const q of envSciQuestions) {
    const code = envSciTopicToGroup(q.topic);
    if (!code) continue;
    const s = stats.get(code) ?? { total: 0, nineCandidates: 0 };
    s.total += 1;
    if (q.archetype === 'envsci_9' || q.archetype === 'envsci_25') s.nineCandidates += 1;
    stats.set(code, s);
  }
  return [...stats.entries()]
    .map(([code, s]) => ({
      code,
      label: ENV_SCI_CONTENT_GROUPS[code] ?? code,
      total: s.total,
      enabled: s.nineCandidates >= 2,
    }))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
}

const GCSE_SCIENCE_LABELS = { biology: 'Biology', chemistry: 'Chemistry', physics: 'Physics' };

// Subjects that have mock exam support
const MOCK_SUPPORTED_SUBJECTS = new Set(['geography', 'env-science', 'biology', 'chemistry', 'physics']);

export default function MockExamLobby({ onStart, onClose, subject = 'geography', getToken, prepareError }) {
  const isEnvSci   = subject === 'env-science';
  const isGcseSci  = GCSE_SCIENCE_SUBJECTS.has(subject);
  const isSupported = MOCK_SUPPORTED_SUBJECTS.has(subject);
  const mockTypes  = isGcseSci ? GCSE_SCIENCE_MOCK_TYPES : isEnvSci ? ENV_MOCK_TYPES : GEOGRAPHY_MOCK_TYPES;

  const [geographyType, setGeographyType] = useState('physical');
  const [mockType, setMockType]           = useState(mockTypes[0].value);
  const [gcsePaper, setGcsePaper]         = useState(null); // null | 1 | 2

  const envSciGroups = useMemo(() => (isEnvSci ? buildEnvSciContentGroupOptions() : []), [isEnvSci]);
  const firstEnabledGroup = envSciGroups.find((g) => g.enabled)?.code ?? envSciGroups[0]?.code ?? null;
  const [selectedGroup, setSelectedGroup] = useState(firstEnabledGroup);
  const [coverage, setCoverage] = useState(null); // { [groupCode]: { total, answered } } | null

  // When user lands on Topic Mock, fetch a per-group "X of Y answered" summary.
  useEffect(() => {
    if (!isEnvSci || mockType !== 'topic_mock' || !getToken || coverage !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const allIds = envSciQuestions.map((q) => q.id);
        const token = await getToken();
        if (cancelled) return;
        const res = await fetch(`${API_BASE}/api/mock-exam/attempt-history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ questionIds: allIds }),
        });
        if (cancelled || !res.ok) {
          if (!cancelled) setCoverage({});
          return;
        }
        const data = await res.json();
        const history = data.history ?? {};
        const byGroup = {};
        for (const q of envSciQuestions) {
          const code = envSciTopicToGroup(q.topic);
          if (!code) continue;
          if (!byGroup[code]) byGroup[code] = { total: 0, answered: 0 };
          byGroup[code].total += 1;
          if (history[q.id]?.count > 0) byGroup[code].answered += 1;
        }
        if (!cancelled) setCoverage(byGroup);
      } catch {
        if (!cancelled) setCoverage({});
      }
    })();
    return () => { cancelled = true; };
  }, [isEnvSci, mockType, getToken, coverage]);

  const rules = getRules(subject, mockType);
  const isTopicMock = isEnvSci && mockType === 'topic_mock';
  const startDisabled = isTopicMock && (!selectedGroup || !envSciGroups.find((g) => g.code === selectedGroup)?.enabled);

  if (!isSupported) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center">
        <div className="text-[10px] font-mono text-[#8b949e] tracking-widest uppercase mb-4">Mock Exam</div>
        <p className="text-[#e6edf3] text-sm mb-2">Mock exams for this subject are coming soon.</p>
        <button onClick={onClose} className="mt-6 w-full max-w-xs py-2 rounded-lg text-[#8b949e] hover:text-[#e6edf3] text-xs transition-colors">
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center overflow-y-auto">
      <div className="w-full max-w-md">
        <div className="mb-2 text-[10px] font-mono text-[#8b949e] tracking-widest uppercase">
          {isGcseSci
            ? `AQA GCSE ${GCSE_SCIENCE_LABELS[subject]}`
            : isEnvSci
            ? 'AQA Environmental Science A-Level'
            : 'AQA Geography A-Level'}
        </div>
        <h1 className="text-2xl font-bold text-[#e6edf3] mb-1">Mock Exam</h1>
        <p className="text-[#8b949e] text-sm mb-8">
          Simulate real exam conditions. No help, no reruns.
        </p>

        {/* Mock type toggle */}
        <div className="mb-6 text-left">
          <p className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider mb-3">
            Exam Type
          </p>
          <div className="grid grid-cols-2 gap-2">
            {mockTypes.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setMockType(opt.value)}
                className={`rounded-xl border p-3 text-left transition-colors ${
                  mockType === opt.value
                    ? 'border-[#58a6ff]/60 bg-[#58a6ff]/10'
                    : 'border-[#30363d] bg-[#161b22] hover:border-[#8b949e]'
                }`}
              >
                <div className={`text-xs font-semibold mb-1 ${mockType === opt.value ? 'text-[#58a6ff]' : 'text-[#e6edf3]'}`}>
                  {opt.label}
                </div>
                <div className="text-[10px] text-[#8b949e] leading-relaxed">{opt.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* GCSE science paper filter */}
        {isGcseSci && (
          <div className="mb-6 text-left">
            <p className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider mb-3">
              Paper Focus
            </p>
            <div className="grid grid-cols-3 gap-2">
              {GCSE_SCIENCE_PAPER_OPTIONS.map((opt) => (
                <button
                  key={String(opt.value)}
                  onClick={() => setGcsePaper(opt.value)}
                  className={`rounded-xl border p-3 text-left transition-colors ${
                    gcsePaper === opt.value
                      ? 'border-[#238636] bg-[#238636]/10'
                      : 'border-[#30363d] bg-[#161b22] hover:border-[#8b949e]'
                  }`}
                >
                  <div className={`text-xs font-semibold mb-1 ${gcsePaper === opt.value ? 'text-[#3fb950]' : 'text-[#e6edf3]'}`}>
                    {opt.label}
                  </div>
                  <div className="text-[10px] text-[#8b949e] leading-relaxed">{opt.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Geography type toggle — only for geography */}
        {!isEnvSci && !isGcseSci && (
          <div className="mb-6 text-left">
            <p className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider mb-3">
              {mockType === 'full_mock' ? 'Paper' : 'Geography Type'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {GEOGRAPHY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setGeographyType(opt.value)}
                  className={`rounded-xl border p-3 text-left transition-colors ${
                    geographyType === opt.value
                      ? 'border-[#238636] bg-[#238636]/10'
                      : 'border-[#30363d] bg-[#161b22] hover:border-[#8b949e]'
                  }`}
                >
                  <div className={`text-xs font-semibold mb-1 ${geographyType === opt.value ? 'text-[#3fb950]' : 'text-[#e6edf3]'}`}>
                    {opt.label}
                  </div>
                  <div className="text-[10px] text-[#8b949e] leading-relaxed">
                    {mockType === 'full_mock' ? opt.topicsFull : opt.topicsFour}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Env-science content group picker — only for Topic Mock */}
        {isTopicMock && (
          <div className="mb-6 text-left">
            <p className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider mb-3">
              Content Group
            </p>
            <div className="grid grid-cols-1 gap-1.5">
              {envSciGroups.map((opt) => {
                const cov = coverage?.[opt.code];
                const isSelected = selectedGroup === opt.code;
                return (
                  <button
                    key={opt.code}
                    onClick={() => opt.enabled && setSelectedGroup(opt.code)}
                    disabled={!opt.enabled}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${
                      isSelected && opt.enabled
                        ? 'border-[#58a6ff]/60 bg-[#58a6ff]/10'
                        : opt.enabled
                        ? 'border-[#30363d] bg-[#161b22] hover:border-[#8b949e]'
                        : 'border-[#30363d]/60 bg-[#0d1117] opacity-40 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <div className={`text-xs font-semibold ${isSelected && opt.enabled ? 'text-[#58a6ff]' : 'text-[#e6edf3]'}`}>
                        {opt.label}
                      </div>
                      <div className="text-[9px] font-mono text-[#8b949e] flex-shrink-0">
                        {coverage === null
                          ? '…'
                          : cov
                          ? `${cov.answered}/${cov.total} answered`
                          : `${opt.total} qs`}
                      </div>
                    </div>
                    {!opt.enabled && (
                      <div className="text-[9px] font-mono text-[#8b949e] mt-1">
                        Not enough 9-mark questions in this group
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {prepareError && (
          <div className="mb-4 px-3 py-2 rounded border border-[#f85149]/40 bg-[#f85149]/10 text-[#f85149] text-xs font-mono text-left">
            {prepareError}
          </div>
        )}

        {/* Rules */}
        <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-5 mb-6 text-left space-y-3">
          <p className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider mb-3">
            Exam Rules
          </p>
          {rules.map((rule, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="text-[#8b949e] text-xs mt-0.5 w-4 flex-shrink-0 text-center">
                {rule.icon}
              </span>
              <span className="text-[#e6edf3] text-xs leading-relaxed">{rule.text}</span>
            </div>
          ))}
        </div>

        <button
          onClick={() => onStart(geographyType, mockType, isTopicMock ? selectedGroup : undefined, isGcseSci ? gcsePaper : undefined)}
          disabled={startDisabled}
          className="w-full py-3 rounded-lg bg-[#238636] hover:bg-[#2ea043] text-white text-sm font-semibold transition-colors mb-3 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Start Exam
        </button>
        <button
          onClick={onClose}
          className="w-full py-2 rounded-lg text-[#8b949e] hover:text-[#e6edf3] text-xs transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
